// src/demo/DemoDataProvider.jsx
//
// Demo Mode · React context that exposes the canonical seed (§4)
// + sandbox mutations to wrapped screens. Screens consume via the
// useDemoData() hook below.
//
// Pattern (per build prompt §5):
//   - When `demo_mode` is true, the existing real screens render
//     with demo data sourced from this provider.
//   - When `demo_mode` is false, this provider isn't mounted and
//     screens fetch from Supabase as usual.
//
// Sandbox mutations (Phase 5):
//   - The provider also holds the per-session mutation layer
//     (moved leads, sent demo SMS, scenarios played). Mutations
//     persist to `demo_sessions.state` JSONB so they survive page
//     reloads.
//   - On exit (Phase 6), the demo_sessions row is purged.
//
// NO PROVIDER WRITES TO PRODUCTION TABLES. The §2 seal enforces
// this at the boundary; this provider enforces it at the data
// layer (we literally cannot write to a prod table from here —
// we don't even import the production supabase client).

import React from 'react';
import DEMO_SEED from './seed';

const DemoDataContext = React.createContext(null);

/**
 * useDemoData() — screens consume this when they want demo data.
 * Returns null when not inside a DemoDataProvider (i.e. live mode),
 * so existing screens can do:
 *
 *   const demo = useDemoData();
 *   const leads = demo ? demo.leads : liveLeadsFromSupabase;
 */
export function useDemoData() {
  return React.useContext(DemoDataContext);
}

/**
 * useDemoLeads() — convenience wrapper. Returns the leads array
 * when in demo mode, otherwise null (caller falls back to its own
 * Supabase fetch).
 */
export function useDemoLeads()           { const d = useDemoData(); return d?.leads || null; }
export function useDemoInboxThreads()    { const d = useDemoData(); return d?.inbox_threads || null; }
export function useDemoCalls()           { const d = useDemoData(); return d?.calls || null; }
export function useDemoCalendar()        { const d = useDemoData(); return d?.calendar || null; }
export function useDemoCommissions()     { const d = useDemoData(); return d?.commissions || null; }
export function useDemoBookOfBusiness()  { const d = useDemoData(); return d?.book_of_business || null; }
export function useDemoAiEmployees()     { const d = useDemoData(); return d?.ai_employees || null; }
export function useDemoSampleWorkflow()  { const d = useDemoData(); return d?.sample_workflow || null; }
export function useDemoScenarios()       { const d = useDemoData(); return d?.scenarios || null; }
export function useDemoActivityFeedPool(){ const d = useDemoData(); return d?.activity_feed_pool || null; }
export function useDemoHealthClients()    { const d = useDemoData(); return d?.health_clients   || null; }
export function useDemoLifeClients()      { const d = useDemoData(); return d?.life_clients     || null; }
export function useDemoHealthPolicies()   { const d = useDemoData(); return d?.health_policies  || null; }
export function useDemoReferralPartners() { const d = useDemoData(); return d?.referral_partners|| null; }

/**
 * useIsDemoMode() — true when inside a DemoDataProvider. Lets a
 * screen show a "(demo)" affordance on action buttons that would
 * normally fire a real provider.
 */
export function useIsDemoMode() {
  return useDemoData() != null;
}

/**
 * DemoDataProvider — wrap the app (or a subtree) to put it in
 * demo mode. Initial state is the canonical seed; mutations layer
 * on top via the dispatch helpers.
 *
 * Props:
 *   initialState — optional override (used by Replay to re-seed
 *                  with fresh data; defaults to DEMO_SEED)
 */
export default function DemoDataProvider({ initialState = DEMO_SEED, children }) {
  // We clone the seed once so mutations don't accidentally mutate
  // the shared module-level constant (which would persist across
  // Replay → next visitor).
  const [state, setState] = React.useState(() => deepClone(initialState));

  // Sandbox dispatchers — Phase 5 will wire UI to these. Kept
  // here in Phase 3 so the shape is committed up front and Phase
  // 5 doesn't have to refactor.
  const dispatch = React.useMemo(() => ({
    /**
     * Move a lead between stages. The mock's drag-and-drop calls
     * this; the live screens just need to render the result.
     */
    moveLead(leadId, toStage) {
      setState(prev => ({
        ...prev,
        leads: prev.leads.map(l => l.id === leadId ? { ...l, stage: toStage } : l),
      }));
    },
    /**
     * Append a simulated outbound message to an inbox thread.
     * Per §2, this is presentation-layer only; the message is NOT
     * sent anywhere.
     */
    appendDemoMessage(threadId, body, via = 'NOVA') {
      setState(prev => ({
        ...prev,
        inbox_threads: prev.inbox_threads.map(t => t.id !== threadId ? t : {
          ...t,
          last_message_at: new Date().toISOString(),
          unread: false,
          messages: [...t.messages, {
            id: `m_demo_${Math.random().toString(36).slice(2, 8)}`,
            from: 'agent',
            via,
            at: new Date().toISOString(),
            body,
          }],
        }),
      }));
    },
    /**
     * Advance the sample workflow one step. The animation calls
     * this on each tick.
     */
    advanceSampleWorkflow() {
      setState(prev => {
        const wf = prev.sample_workflow;
        if (!wf || wf.current_step_index >= wf.steps.length - 1) return prev;
        const next = wf.current_step_index + 1;
        return {
          ...prev,
          sample_workflow: {
            ...wf,
            current_step_index: next,
            steps: wf.steps.map((s, i) => ({
              ...s,
              done: i < next,
              current: i === next,
            })),
          },
        };
      });
    },
    /**
     * Replay — re-seed the sandbox from the canonical seed. Used
     * by the Replay Demo control in the shell.
     */
    replay() {
      setState(deepClone(initialState));
    },
  }), [initialState]);

  const value = React.useMemo(() => ({
    ...state,
    dispatch,
    isDemo: true,
  }), [state, dispatch]);

  return (
    <DemoDataContext.Provider value={value}>
      {children}
    </DemoDataContext.Provider>
  );
}

// Defensive deep clone — JSON path is fine for the seed shape
// (no Dates, no functions, no circular refs).
function deepClone(o) {
  return JSON.parse(JSON.stringify(o));
}
